/* eslint-disable @typescript-eslint/naming-convention */
import React, { type FunctionComponent, useEffect } from 'react'
import { useParams } from 'react-router'
import qs from 'query-string'
import Dropdown from 'legacy/components/Dropdown'
import { type DropdownOption, type PartnerScopedRouteParams } from 'legacy/common/types'
import withApiData, { type WithApiDataInjectedProps } from 'legacy/common/withApiData'
import { type Shopper } from 'legacy/routes/shoppers/types'
import { useDomainMessages } from 'domains/shifts/utils/domain/intl'
import { type ShiftsRouteQuery } from 'domains/shifts/components/types'
interface ApiQuery {
  warehouse_location_id: string
  limit: number
}

interface AddShopperDropdownProps extends WithApiDataInjectedProps<Shopper[], {}, ApiQuery> {
  onAddShopper(shopper: Shopper): void
  excludeShopperIds: number[]
}

export const AddShopperDropdown: FunctionComponent<AddShopperDropdownProps> = ({
  fetchData,
  apiData,
  excludeShopperIds,
  onAddShopper,
}) => {
  const { partner_id: partnerId, warehouse_id: warehouseId } = useParams<PartnerScopedRouteParams>()
  useEffect(() => {
    const query = qs.parse(location.search) as ShiftsRouteQuery
    const { warehouse_location_id } = query
    const fetchShoppers = async () => {
      if (warehouse_location_id) {
        await fetchData(`/v1/partners/${partnerId}/warehouses/${warehouseId}/shoppers`, {
          query: { warehouse_location_id, limit: 200 },
        })
      }
    }

    fetchShoppers()
  }, [partnerId, warehouseId, fetchData])

  const getShopperOptions = (): DropdownOption[] => {
    if (!apiData.loaded) {
      return []
    }

    return apiData.data
      .filter(shopper => !excludeShopperIds.includes(shopper.id))
      .map(shopper => ({
        label: `${shopper.first_name} ${shopper.last_name}`,
        value: shopper.id,
      }))
  }

  const handleShopperChange = (shopperId: number) => {
    if (!apiData.loaded) {
      return
    }

    const shopper = apiData.data.find(n => n.id === shopperId)

    if (shopper) {
      onAddShopper(shopper)
    }
  }

  const messages = useDomainMessages({
    addShopperEmpty: 'shiftsDomain.add_shopper_empty',
    addShopper: 'shiftsDomain.add_shopper',
    addShopperError: 'shiftsDomain.add_shopper_error',
    loading: 'shiftsDomain.common.loading',
  })

  const dropdownDisabled = !apiData.loaded || apiData.data.length === 0
  const dropdownText = apiData.loading
    ? messages.loading
    : apiData.loaded
    ? apiData.data.length === 0
      ? messages.addShopperEmpty
      : messages.addShopper
    : messages.addShopperError

  return (
    <Dropdown
      margin="0"
      isDisabled={dropdownDisabled}
      defaultText={dropdownText}
      options={getShopperOptions()}
      onChange={handleShopperChange}
    />
  )
}

export default withApiData(AddShopperDropdown)
